/** Per-station public landing page: /stations/<station_id>.
 *  Public, indexable, share-friendly. Each page gets its own meta tags + OG image. */
import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { usePlayerCtx } from "@/context/PlayerContext";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { ConfidenceBadge } from "@/design";
import { T } from "@/lib/theme";

const C = T.dark;
const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const SITE = "https://www.wavescorepro.com";
const COUNTRY_FLAGS = { Ghana: "🇬🇭", Nigeria: "🇳🇬", "Cote d'Ivoire": "🇨🇮", Senegal: "🇸🇳" };

export default function StationLanding() {
  const { stationId } = useParams();
  const player = usePlayerCtx();
  // ?play=1 deep link focuses the player — never auto-plays (user gesture only).
  const [searchParams] = useSearchParams();
  const playFocus = searchParams.get("play") === "1" && player?.playingId !== stationId;
  const nav = useNavigate();
  const [station, setStation] = useState(null);
  const [live, setLive] = useState(null);
  const [error, setError] = useState("");
  // F-113 — dispute modal state
  const [disputeOpen, setDisputeOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/public/stations/${stationId}`)
      .then((r) => setStation(r.data.station || null))
      .catch((e) => setError(e.response?.status === 404 ? "Station not found." : e.message));
  }, [stationId]);

  useEffect(() => {
    if (!stationId) return;
    let stop = false;
    // F-021: fetch this station's own realtime doc — never collapse "not in
    // the top-20 pulse" into a numeric 0.
    const fetchLive = () =>
      axios.get(`${API_BASE}/stations/${stationId}/realtime`).then((r) => {
        if (stop) return;
        const rt = r.data || {};
        setLive(rt.estimated_total_people != null ? rt : null);
      }).catch(() => {});
    fetchLive();
    const t = setInterval(fetchLive, 30000);
    return () => { stop = true; clearInterval(t); };
  }, [stationId]);

  if (error) {
    return (
      <div style={{ background: C.bg, minHeight: "100vh", color: C.text, padding: 40, fontFamily: "'Inter',sans-serif" }}>
        <h1>Station not found</h1>
        <Link to="/listen" style={{ color: T.greenAccent }}>← Back to all stations</Link>
      </div>
    );
  }
  if (!station) {
    return <div style={{ background: C.bg, minHeight: "100vh" }} data-testid="station-landing-loading" />;
  }

  const flag = COUNTRY_FLAGS[station.country] || "🌍";
  const ogImg = `${SITE}/api/og/stations/${stationId}.svg`;
  const pageUrl = `${SITE}/stations/${stationId}`;
  const title = `${station.name} — Live listener data | WaveScorePro`;
  const desc = `${station.name} (${station.frequency || ""} ${station.city || station.country}) — real-time digital connection counts + daypart heatmap on WaveScorePro. Demographic breakdowns arriving with Phase 2.`;

  return (
    <div data-testid="station-landing-page" style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Inter',sans-serif" }}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:image" content={ogImg} />
        <meta property="og:url" content={pageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={desc} />
        <meta name="twitter:image" content={ogImg} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "RadioStation",
          "name": station.name,
          "url": pageUrl,
          "broadcastFrequency": station.frequency,
          "broadcastDisplayName": station.name,
          "address": { "@type": "PostalAddress", "addressLocality": station.city || station.country, "addressCountry": station.country_code || "GH" },
          ...(station.stream_url ? { "broadcastServiceTier": "FM", "potentialAction": { "@type": "ListenAction", "target": station.stream_url } } : {}),
        })}</script>
      </Helmet>

      <style>{`body{margin:0;background:${C.bg}} @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@700;800&display=swap')`}</style>

      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "14px 28px",
        display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" style={{ textDecoration: "none", color: C.text, fontWeight: 800, fontSize: 18 }}>
          WaveScore<span style={{ color: T.greenAccent }}>Pro</span>
        </Link>
        <div style={{ display: "flex", gap: 10 }}>
          <button data-testid="station-play-cta" onClick={() => player?.toggle({ id: stationId, name: station?.name, frequency: station?.frequency, logo_url: station?.logo_url })}
            style={{ background: playFocus ? T.greenAccent : "transparent",
              color: playFocus ? "#04141F" : T.greenAccent, border: `1px solid ${T.greenAccent}`,
              padding: "7px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 800,
              boxShadow: playFocus ? `0 0 0 4px ${T.greenAccent}44` : "none" }}>
            {player?.playingId === stationId ? "❚❚ Pause" : (playFocus ? "▶ Tap to listen" : "▶ Listen live")}
          </button>
          <button data-testid="station-listen" onClick={() => nav("/listen")}
            style={{ background: "transparent", color: C.text, border: `1px solid ${C.border}`,
              padding: "7px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
            📻 All stations
          </button>
          <button data-testid="station-claim-cta" onClick={() => nav("/claim")}
            style={{ background: T.greenAccent, color: "#fff", border: "none",
              padding: "7px 18px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
            Claim this station →
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 980, margin: "0 auto", padding: "40px 28px 80px" }}>
        <div style={{ color: T.greenAccent, fontSize: 11, fontWeight: 800, letterSpacing: "0.16em", marginBottom: 12 }}>
          {flag} {station.country?.toUpperCase()} · {station.region_name || station.city || "Radio"}
        </div>
        <h1 data-testid="station-name" style={{
          fontFamily: "'Space Grotesk',sans-serif", fontSize: "clamp(36px,6vw,60px)",
          fontWeight: 800, margin: "0 0 8px", letterSpacing: "-0.02em", lineHeight: 1.05,
        }}>
          {station.name}
        </h1>
        <p style={{ color: C.muted, fontSize: 17, lineHeight: 1.6, margin: "0 0 32px", maxWidth: 680 }}>
          {station.frequency ? `${station.frequency} FM · ` : ""}
          {station.city || station.country} · {station.genre || "General"}.{" "}
          Real-time digital connection counts + daypart trends from
          WaveScorePro. Demographic breakdowns arrive with Phase 2.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12, marginBottom: 32 }}>
          <Stat label="Listeners right now"
            value={live?.estimated_total_people != null ? live.estimated_total_people.toLocaleString() : "—"}
            sub={live?.estimated_total_people != null ? null : "Not currently measured"}
            badge={live?.estimated_total_people != null ? (live.confidence_label || live.data_source || "MODEL") : null}
            accent data-testid="station-listeners-stat" />
          <Stat label="Status" value={(live?.is_online ? "Live · streaming" : station.is_streaming ? "Streaming idle" : "FM only")} />
          <Stat label="Frequency" value={station.frequency || "—"} />
          <Stat label="Genre" value={station.genre || "General"} />
        </div>

        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14,
          padding: 22, marginBottom: 24, display: "flex", flexWrap: "wrap", gap: 18, alignItems: "center" }}>
          <div style={{ flex: "1 1 280px" }}>
            <div style={{ color: C.text, fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
              Manage {station.name}?
            </div>
            <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.5 }}>
              If you're the station owner or programme director, claim this profile and unlock the full StationDash —
              daypart heatmap, transmission certificates, and a real-time embeddable widget. Demographics arrive with Phase 2.
            </div>
          </div>
          <button onClick={() => nav("/claim")}
            style={{ background: T.greenAccent, color: "#fff", border: "none", padding: "12px 22px",
              borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 700 }}>
            Claim this station
          </button>
        </div>

        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 22 }}>
          <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>
            About this listing
          </div>
          <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.65, margin: 0 }}>
            This is a public listing for <b style={{ color: C.text }}>{station.name}</b>. Listener counts shown here are
            derived from live stream telemetry and statistical modelling, in line with our published{" "}
            <Link to="/methodology" style={{ color: C.cyan }}>methodology</Link>. Demographics and verified ratings are
            available to <Link to="/billing" style={{ color: C.cyan }}>Pro and Agency subscribers</Link>.
          </p>
          <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.65, margin: "10px 0 0" }}>
            Represent this station and believe this data is wrong?{" "}
            <button data-testid="station-dispute-link" onClick={() => setDisputeOpen(true)}
              style={{ background: "none", border: "none", padding: 0, color: C.cyan,
                cursor: "pointer", font: "inherit", textDecoration: "underline" }}>
              Dispute or correct this data
            </button>
            {" "}or <Link to="/claim" style={{ color: C.cyan }}>claim this station</Link> to manage it directly.
          </p>
        </div>
      </div>

      {/* F-113 — dispute modal */}
      {disputeOpen && (
        <DisputeModal
          stationId={stationId}
          stationName={station.name}
          currentListeners={live?.estimated_total_people}
          onClose={() => setDisputeOpen(false)}
        />
      )}

      <footer style={{ borderTop: `1px solid ${C.border}`, padding: "20px", textAlign: "center", color: C.muted, fontSize: 12 }}>
        © {new Date().getFullYear()} I-Assist Pro Limited · <Link to="/terms" style={{ color: C.muted }}>Terms</Link> · <Link to="/privacy" style={{ color: C.muted }}>Privacy</Link>
      </footer>
    </div>
  );
}

function Stat({ label, value, accent = false, sub = null, badge = null, "data-testid": tid }) {
  return (
    <div data-testid={tid} aria-label={sub ? `${label}: ${sub}` : undefined}
      style={{ background: C.surface, border: `1px solid ${accent ? T.greenAccent + "55" : C.border}`,
      borderRadius: 10, padding: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</div>
        {badge && <ConfidenceBadge source={badge} size="xs" tone="dark" />}
      </div>
      <div style={{ color: accent ? T.greenAccent : C.text, fontSize: 24, fontWeight: 800,
        fontFamily: "'Space Grotesk',sans-serif" }}>{value}</div>
      {sub && <div style={{ color: C.muted, fontSize: 11, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function DisputeModal({ stationId, stationName, currentListeners, onClose }) {
  const [form, setForm] = useState({
    email: "",
    role: "listener",
    figure_disputed: currentListeners != null
      ? `listeners_now = ${currentListeners.toLocaleString()}`
      : "",
    correct_value: "",
    reasoning: "",
  });
  const [state, setState] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");

  const submit = async () => {
    if (!/.+@.+\..+/.test(form.email) || form.reasoning.length < 10 || !form.figure_disputed) {
      setErrorMsg("Please provide a valid email, the figure you dispute, and at least 10 characters of reasoning.");
      return;
    }
    setState("sending");
    setErrorMsg("");
    try {
      await axios.post(`${API_BASE}/public/station-disputes`, {
        station_id: stationId,
        ...form,
      });
      setState("sent");
    } catch (e) {
      setState("error");
      setErrorMsg(e.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <div data-testid="dispute-modal" role="dialog" aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 400, background: "rgba(10,22,40,0.72)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 14, padding: 28, maxWidth: 520,
          width: "100%", maxHeight: "90vh", overflowY: "auto", color: T.navy }}>
        <h2 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 800 }}>
          Dispute {stationName} data
        </h2>
        <p style={{ margin: "0 0 18px", fontSize: 13, color: T.muted, lineHeight: 1.55 }}>
          Our data team reviews every submission within 3 business days. Rate limits apply
          to prevent spam. Please include the specific figure and a factual reason.
        </p>

        {state === "sent" ? (
          <div data-testid="dispute-sent" style={{ padding: "24px 18px",
            background: "#F0FDF4", border: `1px solid #86EFAC`, borderRadius: 10,
            textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>✓</div>
            <div style={{ fontWeight: 700, color: "#166534", fontSize: 15 }}>
              Dispute submitted
            </div>
            <div style={{ color: "#166534", fontSize: 12, marginTop: 8 }}>
              Reference has been logged. You&apos;ll hear back at {form.email} within 3 business days.
            </div>
            <button data-testid="dispute-close-sent" onClick={onClose}
              style={{ marginTop: 18, padding: "10px 22px", background: T.green,
                color: "#fff", border: "none", borderRadius: 8, fontWeight: 700,
                fontSize: 13, cursor: "pointer" }}>Close</button>
          </div>
        ) : (
          <>
            <Row label="Your email">
              <input data-testid="dispute-email" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@company.com"
                style={inputStyle} />
            </Row>
            <Row label="You are a">
              <select data-testid="dispute-role" value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                style={inputStyle}>
                <option value="listener">Listener / member of the public</option>
                <option value="station_owner">Station owner / employee</option>
                <option value="advertiser">Advertiser / agency</option>
                <option value="other">Other</option>
              </select>
            </Row>
            <Row label="Figure you dispute">
              <input data-testid="dispute-figure" value={form.figure_disputed}
                onChange={(e) => setForm({ ...form, figure_disputed: e.target.value })}
                placeholder='e.g. "listeners_now = 22,920"'
                style={inputStyle} />
            </Row>
            <Row label="Correct value (optional)">
              <input data-testid="dispute-correct" value={form.correct_value}
                onChange={(e) => setForm({ ...form, correct_value: e.target.value })}
                placeholder="What do you believe the correct value is?"
                style={inputStyle} />
            </Row>
            <Row label="Reasoning">
              <textarea data-testid="dispute-reasoning" value={form.reasoning}
                onChange={(e) => setForm({ ...form, reasoning: e.target.value })}
                placeholder="Explain why the figure is wrong. Include evidence if you have it."
                rows={4} maxLength={2000}
                style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} />
              <div style={{ fontSize: 10, color: T.muted, textAlign: "right", marginTop: 2 }}>
                {form.reasoning.length}/2000
              </div>
            </Row>
            {errorMsg && (
              <div data-testid="dispute-error" style={{ color: "#DC2626", fontSize: 12,
                padding: "8px 10px", background: "#FEE2E2", borderRadius: 6, marginBottom: 12 }}>
                {errorMsg}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
              <button data-testid="dispute-cancel" onClick={onClose}
                style={{ background: "transparent", border: `1px solid ${T.border}`,
                  padding: "10px 18px", borderRadius: 8, cursor: "pointer",
                  color: T.muted, fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button data-testid="dispute-submit" onClick={submit} disabled={state === "sending"}
                style={{ background: T.green, border: "none", padding: "10px 22px",
                  borderRadius: 8, cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 700,
                  opacity: state === "sending" ? 0.6 : 1 }}>
                {state === "sending" ? "Sending…" : "Submit dispute"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "10px 12px", background: "#F9FAFB",
  border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 13,
  color: T.navy, outline: "none", fontFamily: "inherit",
};

function Row({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: T.muted,
        textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

